import * as React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ParentProps } from 'types/explorer'
import type { Track, TrackId } from 'types/track'
import { getSimilarTracks } from '@disco/state-management/actions/SearchActions'
import { TRACK_PLAYLISTED_FROM_SIM_TAB } from '@disco/constants/EventTypes'
import { getTrack } from '@disco/state-management/selectors/tracks'
import PaneItems from './../pane-items'
import PaneItemsSkeleton from './../pane-items-skeleton'
import SimilarityAlert from './similarity-alert'
import styles from './index.module.css'

type Props = {
  parentProps: ParentProps
  trackId: TrackId
  aggregatedTrackURL?: string
}
type State = null | {
  err?: string
  results?: Track[] | null | undefined
}
const excludeErrors = ['No embeddings found for the provided audio ID.']

const Similarity = ({
  parentProps,
  trackId,
  aggregatedTrackURL,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const track = useSelector(state =>
    getTrack(state, {
      id: trackId,
    }),
  )
  const [sim, setSim] = useState<State>(null)
  useEffect(() => {
    ;(async function () {
      try {
        const results = await dispatch(
          getSimilarTracks(trackId, aggregatedTrackURL),
        )
        setSim({
          results,
        })
      } catch (err) {
        if (!excludeErrors.includes(err.message)) {
          setSim({
            err: err.message,
          })
        } else {
          setSim({
            results: null,
          })
        }
      }
    })()
  }, [dispatch, trackId])
  if (!sim) return <PaneItemsSkeleton />
  const tracksCount = sim.results?.length ?? 0
  const hasResults = tracksCount > 0
  // The search completed successfully but returned no similar tracks. This is
  // distinct from `results` being null/undefined, which SimilarityAlert handles
  // (track still processing or not yet auto-tagged).
  const isEmpty = Array.isArray(sim.results) && tracksCount === 0
  const tracks =
    sim.results?.map(track => ({
      ...track,
      internal_data: {
        ...track.internal_data,
        [TRACK_PLAYLISTED_FROM_SIM_TAB]: {
          panelId: trackId,
        },
      },
    })) || []
  return (
    <>
      {hasResults && (
        <div className={styles.similarityLabel}>
          <span>
            {tracksCount} <span>{tracksCount === 1 ? 'track' : 'tracks'}</span>
          </span>
        </div>
      )}

      <div className={styles.similarityResult}>
        {track && (
          <SimilarityAlert
            errorMessage={sim.err}
            results={sim.results}
            track={track}
          />
        )}

        {isEmpty && (
          <div className={styles.similarityEmpty}>No similar tracks found.</div>
        )}

        {hasResults && (
          <PaneItems
            itemPrefix="similarity"
            items={tracks}
            isSimilarity
            {...parentProps}
          />
        )}
      </div>
    </>
  )
}

export default Similarity
